<template>
  <div class="change col-xl-5 mx-auto">
    <b-form-group 
    label="Copy Existing Officer" 
    v-if="officersForChange.length">
      <b-form-select 
      :value="0"
      :options="officersForChange" 
      @change="copyExistingChanged"
      text-field="officerName" 
      value-field="officerID"
      :disabled="isViewOnly">
          <template slot="first">
              <option :value="0" disabled>-- Select an existing officer to copy (optional) --</option>
          </template>
      </b-form-select>
    </b-form-group>

    <b-form-group label="Name" :state="IsFieldValid($v.officer.officerName)" :invalid-feedback="RequiredAndMaxLengthMessage('Name', $v.officer.officerName)">
      <b-form-input v-model="$v.officer.officerName.$model" :state="IsFieldValid($v.officer.officerName)" :disabled="isViewOnly" />
    </b-form-group>

    <b-form-group label="Title" :state="IsFieldValid($v.officer.officerTitleID)" :invalid-feedback="RequiredMessage('Title')">
        <b-form-select 
          v-model="$v.officer.officerTitleID.$model" 
          type="text" :options="officerTitles" 
          :state="IsFieldValid($v.officer.officerTitleID)" 
          text-field="description" 
          value-field="officerTitleID"
          :disabled="isViewOnly">
            <template slot="first">
                <option :value="0" disabled>-- Officer Title/Type --</option>
            </template>
        </b-form-select>
    </b-form-group>

    <b-form-group label="Phone" :state="IsFieldValid($v.officer.officerPhone)" :invalid-feedback="PhoneMessage()">
      <b-form-input v-model="$v.officer.officerPhone.$model" :state="IsFieldValid($v.officer.officerPhone)" :disabled="isViewOnly" />
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Ownership Interest Type" class="col-6" :invalid-feedback="RequiredMessage('Interest Type')" :state="IsFieldValid($v.officer.interestType)">
          <b-form-radio-group 
            :options="[{text: 'Interest (%)', value: 0}, {text: 'Shares (#)', value: 1}]" 
            v-model="$v.officer.interestType.$model"
            :disabled="isViewOnly"></b-form-radio-group>
        </b-form-group>
        <b-form-group :label="officer.interestType == 1 ? 'Number of Shares' : 'Ownership Interest'" class="col-6" :state="IsFieldValid($v.officer.interest)" :invalid-feedback="interestMessage($v.officer.interest)">
          <b-form-input v-model="$v.officer.interest.$model" type="number" :state="IsFieldValid($v.officer.interest)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { required, maxLength, minValue, maxValue, decimal } from 'vuelidate/lib/validators'
import CustomValidations from '../mixins/customValidations.js'
const phoneValidation = CustomValidations.phoneValidation

export default {
  name: 'Officer',
  props: {
    officerProp: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      officer: this.officerProp,
      dirty: false
    }
  },
  validations() {
    return {
      officer: {
        officerName: {
          required,
          maxLength: maxLength(50)
        },
        officerTitleID: {
          minValue: minValue(1)
        },
        officerPhone: {
          phoneValidation
        },
        interest: {
          required,
          decimal,
          minValue: minValue(0),
          maxValue: maxValue(this.officer.interestType == 1 ? 9999999999 : 100)
        },
        interestType: {
          required
        }
      }
    }
  },
  methods: {
    copyExistingChanged(officerId) {
      const officer = this.$store.state.officer.officers.find(o => o.officerID == officerId)
      if (!officer) return
      
      this.$v.officer.officerName.$model = officer.officerName
      this.$v.officer.officerTitleID.$model = officer.officerTitleID
      this.$v.officer.officerPhone.$model = officer.officerPhone
      this.$v.officer.interestType.$model = ''
      this.$v.officer.interest.$model = ''
    },
    interestMessage(field) {
      const name = this.officer.interestType == 1 ? 'Shares' : 'Interest'
      return !field.required ? this.RequiredMessage(name) : this.officer.interestType == 1 ? `Shares must be between 0 and 9999999999.` : `Interest must be between 0 and 100.`
    },
    backClicked() {
      this.$router.push({
        name: 'officer/list',
        params: {
          submissionId: this.submission.submissionID,
          changeId: this.change.changeID,
          entityId: this.officer.entityID
        }
      })
    },
    nextClicked() {
      if (this.isViewOnly) {
        this.$router.push({
          name: 'officer/list',
          params: {
            submissionId: this.submission.submissionID,
            changeId: this.change.changeID,
            entityId: this.officer.entityID
          }
        })
      } else {      
        this.$v.$touch()
        if (this.$v.$invalid) return

        this.$store.dispatch('officer/save', this.officer).then(() => {
          this.dirty = false
          this.$router.push({
            name: 'officer/list',
            params: {
              submissionId: this.submission.submissionID,
              changeId: this.change.changeID,
              entityId: this.officer.entityID
            }
          })
        })
      }
    }
  },
  computed: {
    officersForChange() {
      return this.$store.state.officer.officers.filter(o => !o.documentID && this.$store.state.entity.entities.filter(e => e.changeID == this.change.changeID && e.entityID == o.entityID).length)
    },
    ...mapState({
      submission: state => state.submission.submission,
      change: state => state.change.change,
      changeTypes: state => state.application.changeTypes,
      officerTitles: state => state.application.officerTitles
    }),
    ...mapGetters({
      isViewOnly: 'application/isChangeViewOnly'
    })
  },
  watch: {
    officer: {
      handler() {
        this.dirty = true
      },
      deep: true
    }
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty) return next()
    
    this.ConfirmLeave().then(answer => {
      if (answer == this.ConfirmLeaveEventTypes.Discard) return next()
      else if (answer == this.ConfirmLeaveEventTypes.Save) {
        this.$v.$touch()
        if (this.$v.$invalid) return next(false)
        else return this.$store.dispatch('officer/save', this.officer).then(() => next())
      }
      else return next(false)
    })
  }
}
</script>